import { useEffect, useState } from 'react';

/**
 * Modal that appears when a check-in attempt is rejected as too late.
 * The user types a reason, hits Confirm, and the check-in is retried with the
 * reason attached.
 *
 * Props:
 *   open        — boolean
 *   contextLine — { title?: string, detail?: string } shown in the header
 *   onConfirm(reason)
 *   onDismiss()
 */
export function LateCheckInModal({ open, contextLine, onConfirm, onDismiss }) {
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (!open) {
            setReason('');
            return;
        }
        const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onDismiss]);

    if (!open) return null;

    const ready = reason.trim().length >= 5;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 50,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="late-modal-title"
                className="surface-card"
                style={{ padding: 22, width: '100%', maxWidth: 480 }}
            >
                <div className="flex items-start justify-between" style={{ marginBottom: 12 }}>
                    <h2 id="late-modal-title" style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>
                        {contextLine?.title || "You're checking in late"}
                    </h2>
                    <button
                        onClick={onDismiss}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--color-text-tertiary)',
                            fontSize: 16,
                            cursor: 'pointer',
                            lineHeight: 1,
                        }}
                    >
                        ✕
                    </button>
                </div>

                {contextLine?.detail && (
                    <p
                        style={{
                            fontSize: 13,
                            color: 'var(--color-text-secondary)',
                            background: 'var(--color-pill-pending-bg)',
                            border: '0.5px solid rgba(99, 56, 6, 0.18)',
                            borderRadius: 'var(--border-radius-md)',
                            padding: '8px 12px',
                            marginBottom: 14,
                            lineHeight: 1.5,
                        }}
                    >
                        {contextLine.detail}
                    </p>
                )}

                <label
                    htmlFor="late-reason"
                    style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--color-text-secondary)',
                        marginBottom: 6,
                    }}
                >
                    Reason for late check-in (required, minimum 5 characters)
                </label>
                <textarea
                    id="late-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. Stuck in traffic, doctor's appointment, etc."
                    className="textarea"
                    autoFocus
                    maxLength={500}
                />

                <div
                    className="flex justify-end"
                    style={{
                        gap: 8,
                        paddingTop: 12,
                        marginTop: 12,
                        borderTop: '0.5px solid var(--color-border-tertiary)',
                    }}
                >
                    <button onClick={onDismiss} className="btn btn-ghost">Cancel</button>
                    <button
                        onClick={() => onConfirm(reason.trim())}
                        disabled={!ready}
                        className="btn btn-primary"
                    >
                        Check in
                    </button>
                </div>
            </div>
        </div>
    );
}
